import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Copy, Plus, Star, Trash2, Play, X } from 'lucide-react'
import { AreaChart, Area, ResponsiveContainer, YAxis } from 'recharts'
import './SubAgentPanel.css'

// Agent type from Supabase
interface AgentType {
  id: string
  name: string
  icon: string
  color: string
  description: string
  system_prompt: string
  model: string
  temperature: number
  max_tokens: number
  is_builtin: boolean
  is_favorite: boolean
  use_count: number
}

// Running/completed agent
interface AgentRun {
  task_id: string
  agent_type: string
  task_description: string
  context?: string
  status: 'running' | 'completed' | 'failed'
  output?: string
  tokens_used: number
  cost_usd: number
  created_at: string
  completed_at?: string
  duration_ms?: number
}

// Agent metrics
interface AgentMetrics {
  agent_active: number
  agent_completed_today: number
  agent_tokens_today: number
  agent_cost_today: number
}

const API_BASE = '/api/synapse'
const API_KEY = 'synapse-dev'

const SubAgentPanel: React.FC = () => {
  // Agent types from Supabase
  const [agentTypes, setAgentTypes] = useState<AgentType[]>([])
  const [selectedType, setSelectedType] = useState<string>('')

  // Task input
  const [taskInput, setTaskInput] = useState('')
  const [contextInput, setContextInput] = useState('')

  // Running agents
  const [agents, setAgents] = useState<AgentRun[]>([])
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null)
  const [outputs, setOutputs] = useState<Record<string, string>>({})

  // Metrics
  const [metrics, setMetrics] = useState<AgentMetrics>({
    agent_active: 0,
    agent_completed_today: 0,
    agent_tokens_today: 0,
    agent_cost_today: 0,
  })

  // Chart history
  const [activeHistory, setActiveHistory] = useState<{ value: number }[]>(
    Array(30).fill({ value: 0 })
  )
  const [tokenHistory, setTokenHistory] = useState<{ value: number }[]>(
    Array(30).fill({ value: 0 })
  )

  // UI state
  const [wsConnected, setWsConnected] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newAgent, setNewAgent] = useState({
    name: '',
    icon: '🤖',
    color: '#22d3ee',
    description: '',
    system_prompt: '',
  })

  const wsRef = useRef<WebSocket | null>(null)
  const outputRef = useRef<HTMLPreElement>(null)

  // Fetch agent types
  const fetchAgentTypes = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/agents/types`, {
        headers: { 'X-API-Key': API_KEY },
      })
      const data = await res.json()
      if (data.success && data.agent_types) {
        setAgentTypes(data.agent_types)
        if (!selectedType && data.agent_types.length > 0) {
          setSelectedType(data.agent_types[0].id)
        }
      }
    } catch (e) {
      console.error('Failed to fetch agent types:', e)
    }
  }, [selectedType])

  // Fetch recent agents
  const fetchAgents = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/agents/list`, {
        headers: { 'X-API-Key': API_KEY },
      })
      const data = await res.json()
      if (data.success && data.agents) {
        setAgents(data.agents)
      }
    } catch (e) {
      console.error('Failed to fetch agents:', e)
    }
  }, [])

  // Fetch metrics
  const fetchMetrics = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/agents/metrics`, {
        headers: { 'X-API-Key': API_KEY },
      })
      const data = await res.json()
      if (data.success) {
        setMetrics({
          agent_active: data.agent_active || 0,
          agent_completed_today: data.agent_completed_today || 0,
          agent_tokens_today: data.agent_tokens_today || 0,
          agent_cost_today: data.agent_cost_today || 0,
        })

        // Update history
        setActiveHistory(prev => [...prev.slice(1), { value: data.agent_active || 0 }])
        setTokenHistory(prev => [...prev.slice(1), { value: data.agent_tokens_today || 0 }])
      }
    } catch (e) {
      console.error('Failed to fetch metrics:', e)
    }
  }, [])

  // WebSocket connection
  useEffect(() => {
    const connectWebSocket = () => {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      const wsUrl = `${protocol}//${window.location.host}/api/synapse/ws/tasks?token=${API_KEY}`

      const ws = new WebSocket(wsUrl)

      ws.onopen = () => {
        setWsConnected(true)
        console.log('[SubAgentPanel] WebSocket connected')
      }

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data)

          // Handle agent chunk messages
          if (msg.type === 'agent_chunk' && msg.task_id) {
            setOutputs(prev => ({
              ...prev,
              [msg.task_id]: (prev[msg.task_id] || '') + (msg.chunk || ''),
            }))

            // Update progress
            if (msg.progress !== undefined) {
              setAgents(prev =>
                prev.map(a =>
                  a.task_id === msg.task_id
                    ? { ...a, progress: msg.progress }
                    : a
                )
              )
            }

            // Handle completion
            if (msg.is_final) {
              setAgents(prev =>
                prev.map(a =>
                  a.task_id === msg.task_id
                    ? { ...a, status: 'completed' as const }
                    : a
                )
              )
              fetchAgents()
              fetchMetrics()
            }
          }

          // Handle task updates
          if (msg.type === 'task_updated' && msg.task_id?.startsWith('agent-')) {
            setAgents(prev =>
              prev.map(a =>
                a.task_id === msg.task_id
                  ? { ...a, status: msg.status?.toLowerCase() === 'completed' ? 'completed' : 'running' }
                  : a
              )
            )
          }
        } catch (e) {
          // Ignore parse errors
        }
      }

      ws.onclose = () => {
        setWsConnected(false)
        console.log('[SubAgentPanel] WebSocket closed, reconnecting...')
        setTimeout(connectWebSocket, 3000)
      }

      ws.onerror = () => {
        ws.close()
      }

      wsRef.current = ws
    }

    connectWebSocket()

    return () => {
      wsRef.current?.close()
    }
  }, [fetchAgents, fetchMetrics])

  // Initial data fetch
  useEffect(() => {
    fetchAgentTypes()
    fetchAgents()
    fetchMetrics()

    // Poll metrics every 5s
    const interval = setInterval(fetchMetrics, 5000)
    return () => clearInterval(interval)
  }, [fetchAgentTypes, fetchAgents, fetchMetrics])

  // Auto-scroll output
  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight
    }
  }, [outputs, selectedAgentId])

  // Launch agent
  const launchAgent = async () => {
    if (!taskInput.trim() || !selectedType) return

    const selectedAgent = agentTypes.find(t => t.id === selectedType)
    if (!selectedAgent) return

    // Check concurrent limit
    const runningCount = agents.filter(a => a.status === 'running').length
    if (runningCount >= 5) {
      alert('Max 5 concurrent agents allowed')
      return
    }

    try {
      const res = await fetch(`${API_BASE}/agents/spawn`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY,
        },
        body: JSON.stringify({
          agent_type: selectedAgent.name,
          task: taskInput,
          context: contextInput || undefined,
        }),
      })

      const data = await res.json()
      if (data.success && data.task_id) {
        // Add to local state immediately
        const newRun: AgentRun = {
          task_id: data.task_id,
          agent_type: selectedAgent.name,
          task_description: taskInput,
          context: contextInput || undefined,
          status: 'running',
          tokens_used: 0,
          cost_usd: 0,
          created_at: new Date().toISOString(),
        }
        setAgents(prev => [newRun, ...prev])
        setSelectedAgentId(data.task_id)
        setOutputs(prev => ({ ...prev, [data.task_id]: '' }))

        // Clear inputs
        setTaskInput('')
        setContextInput('')
      }
    } catch (e) {
      console.error('Failed to spawn agent:', e)
    }
  }

  // Create custom agent type
  const createAgentType = async () => {
    if (!newAgent.name.trim() || !newAgent.system_prompt.trim()) return

    try {
      const res = await fetch(`${API_BASE}/agents/types`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY,
        },
        body: JSON.stringify(newAgent),
      })

      const data = await res.json()
      if (data.success) {
        setShowCreateModal(false)
        setNewAgent({
          name: '',
          icon: '🤖',
          color: '#22d3ee',
          description: '',
          system_prompt: '',
        })
        fetchAgentTypes()
      }
    } catch (e) {
      console.error('Failed to create agent type:', e)
    }
  }

  // Toggle favorite
  const toggleFavorite = async (id: string) => {
    try {
      await fetch(`${API_BASE}/agents/types/${id}/favorite`, {
        method: 'POST',
        headers: { 'X-API-Key': API_KEY },
      })
      fetchAgentTypes()
    } catch (e) {
      console.error('Failed to toggle favorite:', e)
    }
  }

  // Delete agent type
  const deleteAgentType = async (id: string) => {
    if (!confirm('Delete this agent type?')) return

    try {
      await fetch(`${API_BASE}/agents/types/${id}`, {
        method: 'DELETE',
        headers: { 'X-API-Key': API_KEY },
      })
      fetchAgentTypes()
    } catch (e) {
      console.error('Failed to delete agent type:', e)
    }
  }

  // Copy output
  const copyOutput = () => {
    if (selectedAgentId && outputs[selectedAgentId]) {
      navigator.clipboard.writeText(outputs[selectedAgentId])
    }
  }

  // Get selected agent type details
  const selectedAgentType = agentTypes.find(t => t.id === selectedType)

  // Get selected agent run
  const selectedAgent = agents.find(a => a.task_id === selectedAgentId)

  // Format numbers
  const formatNumber = (n: number) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`
    return n.toString()
  }

  return (
    <div className="subagent-panel">
      {/* Header */}
      <div className="panel-header">
        <div className="header-left">
          <span className="panel-icon">🤖</span>
          <span className="panel-title">Agents</span>
          <span className="agent-limit">5 max</span>
        </div>
        <div className="header-right">
          <div className={`ws-indicator ${wsConnected ? 'connected' : 'disconnected'}`} />
        </div>
      </div>

      {/* Metrics Row */}
      <div className="metrics-row">
        <div className="metric-card" style={{ borderColor: '#22d3ee' }}>
          <div className="metric-label">Active</div>
          <div className="metric-value" style={{ color: '#22d3ee' }}>
            {metrics.agent_active}
          </div>
        </div>
        <div className="metric-card" style={{ borderColor: '#22c55e' }}>
          <div className="metric-label">Done</div>
          <div className="metric-value" style={{ color: '#22c55e' }}>
            {metrics.agent_completed_today}
          </div>
        </div>
        <div className="metric-card" style={{ borderColor: '#ec4899' }}>
          <div className="metric-label">Tokens</div>
          <div className="metric-value" style={{ color: '#ec4899' }}>
            {formatNumber(metrics.agent_tokens_today)}
          </div>
        </div>
        <div className="metric-card" style={{ borderColor: '#10b981' }}>
          <div className="metric-label">Cost</div>
          <div className="metric-value" style={{ color: '#10b981' }}>
            ${metrics.agent_cost_today.toFixed(4)}
          </div>
        </div>
      </div>

      {/* Mini Charts */}
      <div className="charts-row">
        <div className="mini-chart">
          <div className="chart-title">Active Agents</div>
          <ResponsiveContainer width="100%" height={50}>
            <AreaChart data={activeHistory}>
              <YAxis domain={[0, 'auto']} hide />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#22d3ee"
                fill="#22d3ee"
                fillOpacity={0.2}
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="mini-chart">
          <div className="chart-title">Tokens Used</div>
          <ResponsiveContainer width="100%" height={50}>
            <AreaChart data={tokenHistory}>
              <YAxis domain={[0, 'auto']} hide />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#ec4899"
                fill="#ec4899"
                fillOpacity={0.2}
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Agent Type Selector */}
      <div className="agent-types-section">
        <div className="agent-types-header">
          <span>Agent Type</span>
          <button className="add-agent-btn" onClick={() => setShowCreateModal(true)}>
            <Plus size={14} /> New
          </button>
        </div>
        <div className="agent-types-grid">
          {agentTypes.map(type => (
            <button
              key={type.id}
              className={`agent-type-pill ${selectedType === type.id ? 'selected' : ''}`}
              style={{
                borderColor: selectedType === type.id ? type.color : 'transparent',
                backgroundColor: selectedType === type.id ? `${type.color}20` : undefined,
              }}
              onClick={() => setSelectedType(type.id)}
            >
              <span className="type-icon">{type.icon}</span>
              <span className="type-name">{type.name}</span>
              {type.is_favorite && <Star size={12} className="favorite-star" fill="#f59e0b" />}
            </button>
          ))}
        </div>
        {selectedAgentType && (
          <div className="type-description">{selectedAgentType.description}</div>
        )}
      </div>

      {/* Task Input */}
      <div className="task-input-section">
        <div className="task-input-row">
          <input
            type="text"
            className="task-input"
            placeholder="Describe what you want the agent to do..."
            value={taskInput}
            onChange={e => setTaskInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && launchAgent()}
          />
          <button
            className="launch-btn"
            onClick={launchAgent}
            disabled={!taskInput.trim() || !selectedType}
          >
            <Play size={16} /> Launch
          </button>
        </div>
        <textarea
          className="context-input"
          placeholder="Context (optional): file paths, URLs, or additional notes..."
          value={contextInput}
          onChange={e => setContextInput(e.target.value)}
          rows={2}
        />
      </div>

      {/* Running Agents List */}
      <div className="agents-list-section">
        <div className="agents-list-header">
          <span>Running ({agents.filter(a => a.status === 'running').length})</span>
          <span className="history-label">Recent</span>
        </div>
        <div className="agents-list">
          {agents.slice(0, 10).map(agent => {
            const agentType = agentTypes.find(t => t.name === agent.agent_type)
            return (
              <div
                key={agent.task_id}
                className={`agent-row ${selectedAgentId === agent.task_id ? 'selected' : ''} ${agent.status}`}
                onClick={() => setSelectedAgentId(agent.task_id)}
              >
                <span className="agent-icon">{agentType?.icon || '🤖'}</span>
                <span className="agent-task">{agent.task_description.slice(0, 40)}...</span>
                <span className={`agent-status ${agent.status}`}>
                  {agent.status === 'running' ? '⏳' : agent.status === 'completed' ? '✓' : '✗'}
                </span>
              </div>
            )
          })}
          {agents.length === 0 && (
            <div className="no-agents">No agents yet. Launch one above!</div>
          )}
        </div>
      </div>

      {/* Output Panel */}
      <div className="output-section">
        <div className="output-header">
          <span>Output{selectedAgent ? `: ${selectedAgent.task_description.slice(0, 30)}...` : ''}</span>
          <button className="copy-btn" onClick={copyOutput} disabled={!selectedAgentId}>
            <Copy size={14} /> Copy
          </button>
        </div>
        <pre className="output-content" ref={outputRef}>
          {selectedAgentId && outputs[selectedAgentId]
            ? outputs[selectedAgentId]
            : 'Select an agent to view output...'}
        </pre>
      </div>

      {/* Create Agent Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create Custom Agent</h3>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <label>Name</label>
                <input
                  type="text"
                  value={newAgent.name}
                  onChange={e => setNewAgent({ ...newAgent, name: e.target.value })}
                  placeholder="e.g., Debugger"
                />
              </div>
              <div className="form-row inline">
                <div>
                  <label>Icon</label>
                  <input
                    type="text"
                    value={newAgent.icon}
                    onChange={e => setNewAgent({ ...newAgent, icon: e.target.value })}
                    style={{ width: '60px', textAlign: 'center' }}
                  />
                </div>
                <div>
                  <label>Color</label>
                  <input
                    type="color"
                    value={newAgent.color}
                    onChange={e => setNewAgent({ ...newAgent, color: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-row">
                <label>Description</label>
                <input
                  type="text"
                  value={newAgent.description}
                  onChange={e => setNewAgent({ ...newAgent, description: e.target.value })}
                  placeholder="What does this agent do?"
                />
              </div>
              <div className="form-row">
                <label>System Prompt</label>
                <textarea
                  value={newAgent.system_prompt}
                  onChange={e => setNewAgent({ ...newAgent, system_prompt: e.target.value })}
                  placeholder="You are a... (define the agent's behavior)"
                  rows={4}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowCreateModal(false)}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={createAgentType}
                disabled={!newAgent.name.trim() || !newAgent.system_prompt.trim()}
              >
                Create Agent
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SubAgentPanel
